import React, { useEffect, useState } from "react";
import {
    finishWorkout,
    getWorkout, rerunManipulation,
    resetWeek,
    updateProgram
} from "../../services/PlanSerice";
import {
    Box, Button, Dialog, DialogContent, IconButton, MenuItem, Select, Tooltip, Typography
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import TimerIcon from '@mui/icons-material/Timer';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ProgramExerciseCard from "./ProgramExerciseCard";
import ProgramSuperSetCard from "./ProgramSuperSetCard";
import ProgramSummaryView from "./ProgramSummaryView";
import AddExercisesDialog from "../new_templates/AddExercisesDialog";
import AddSuperSetDialog from "../new_templates/AddSuperSetDialog";
import { useMessageContext } from "../../contexts/MessageContext";
import { getErrorMessage } from "../../enums/ErrorMessages";
import { RTL } from "../../cache/CacheRtl";
import { ManipulationTypes } from "../../enums/ManipulationTypes";
import { createSuperSetLink, groupExercisesForRendering, reorderExerciseGroups, SUPERSET_COLORS, isCardioExercise } from "../../utils/exerciseUtils";

const WorkoutActiveTab = ({ workoutIdx, editMode, planState, setEditMode, traineeId, setLoading, openIntervals, setOpenIntervals, onReload }) => {

    const isSuperSetEnabled = true;

    const [workout, setWorkout] = useState();
    const [prvWorkout, setPrvWorkout] = useState();
    const [reload, setReload] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'summary'

    // Drag-and-drop state for reordering
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (e, startIndex) => {
        setDraggedIndex(startIndex);
        e.dataTransfer.effectAllowed = "move";
    };
    const handleDragEnd = () => { setDraggedIndex(null); setDragOverIndex(null); };
    const handleDragOver = (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== targetIndex) setDragOverIndex(targetIndex);
    };
    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) { setDraggedIndex(null); setDragOverIndex(null); return; }
        setWorkout(prv => {
            let nw = { ...prv };
            const exercises = [...nw.programExercises].sort((a, b) => a.index > b.index ? 1 : -1);
            // Move whole GROUPS: a dragged superset (draggedIndex = the pair's first exercise)
            // relocates both partners instead of orphaning the second. See reorderExerciseGroups.
            const reordered = reorderExerciseGroups(exercises, draggedIndex, targetIndex);
            nw.programExercises = reordered;
            return nw;
        });
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Add dialogs
    const [isAddChooserOpen, setIsAddChooserOpen] = useState(false);
    const [isAddExercisesOpen, setIsAddExercisesOpen] = useState(false);
    const [isAddCardioOpen, setIsAddCardioOpen] = useState(false);
    const [isAddCatalystOpen, setIsAddCatalystOpen] = useState(false);
    const [isAddSuperSetOpen, setIsAddSuperSetOpen] = useState(false);

    const { notificationHandler } = useMessageContext();

    // --- All existing logic preserved exactly ---

    const isAnyWorkoutNotHasFinishedOrWeekIsFinished = () => {
        if (!planState?.plan?.blocks) return false;
        const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curBlock = blocks.at(planState.blockIdx);
        if (!curBlock?.weeks) return false;
        const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curWeek = weeks.at(planState.weekIdx);
        if (!curWeek?.workouts) return false;
        return curWeek.workouts.some(workout => workout.stateType === "COMPLETED") || curWeek.finished;
    };

    const isLastWeekFinished = () => {
        if (planState.blockIdx === 0 && planState.weekIdx === 0) return true;
        if (!planState?.plan?.blocks) return false;
        const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
        if (planState.weekIdx === 0) {
            const prvBlock = blocks.at(planState.blockIdx - 1);
            if (!prvBlock?.weeks) return false;
            const prvWeek = prvBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1).at(prvBlock.weeks.length - 1);
            return prvWeek.finished;
        } else {
            const curBlock = blocks.at(planState.blockIdx);
            const prvWeek = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1).at(planState.weekIdx - 1);
            return prvWeek.finished;
        }
    };

    const getPrvWorkoutId = () => {
        const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curBlock = blocks.at(planState.blockIdx);
        const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curWeek = weeks.at(planState.weekIdx);
        if (curWeek.index > 0) {
            const prvWeek = weeks.at(curWeek.index - 1);
            const prvWorkouts = prvWeek?.workouts?.sort((a, b) => a.index > b.index ? 1 : -1);
            return prvWorkouts?.at(workoutIdx)?.id;
        } else if (curBlock.index > 0) {
            const prvBlock = blocks.at(curBlock.index - 1);
            const prvWeeks = prvBlock?.weeks?.sort((a, b) => a.index > b.index ? 1 : -1);
            const prvWeek = prvWeeks?.at(prvWeeks.length - 1);
            const prvWorkouts = prvWeek?.workouts?.sort((a, b) => a.index > b.index ? 1 : -1);
            return prvWorkouts?.at(workoutIdx)?.id;
        }
    };

    useEffect(() => {
        setLoading(true);
        if (!planState?.plan?.blocks) { setLoading(false); return; }
        const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curBlock = blocks.at(planState.blockIdx);
        if (!curBlock?.weeks) { setLoading(false); return; }
        const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curWeek = weeks.at(planState.weekIdx);
        if (!curWeek?.workouts) { setLoading(false); return; }
        const workouts = curWeek.workouts.sort((a, b) => a.index > b.index ? 1 : -1);
        const curWorkout = workouts.at(workoutIdx);
        if (!curWorkout) { setLoading(false); return; }

        getWorkout(curWorkout.id).then(res => { setWorkout(res.data); setLoading(false); });

        const prvWorkoutId = getPrvWorkoutId();
        if (prvWorkoutId != null) {
            getWorkout(prvWorkoutId).then(res => setPrvWorkout(res.data));
        } else {
            setPrvWorkout(null);
        }
    }, [planState, workoutIdx, reload]);

    const handleFinishWorkout = (workoutId) => {
        finishWorkout(workoutId)
            .then(() => setReload(prv => !prv))
            .then(() => setEditMode(false))
            .then(() => onReload?.())
            .then(() => notificationHandler.success("אימון סויים בהצלחה"));
    };

    const handleResetWeek = (weekId) => {
        resetWeek(weekId)
            .then(() => setReload(prv => !prv))
            .then(() => setEditMode(false))
            .then(() => notificationHandler.success("שבוע אופס בהצלחה"));
    };

    const handleRerunManipulation = (weekIdParam) => {
        if (!planState?.plan?.blocks) return;
        const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curBlock = blocks.at(planState.blockIdx);
        if (!curBlock?.weeks) return;
        const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
        const curWeek = weeks.at(planState.weekIdx);
        if (!curWeek) return;

        let weekId = weekIdParam || null;
        if (!weekId) {
            if (planState.blockIdx === 0 && planState.weekIdx === 0) {
                weekId = curWeek.id;
            } else if (planState.weekIdx === 0) {
                const prvBlock = blocks.at(planState.blockIdx - 1);
                if (prvBlock?.weeks) {
                    const prvBlockWeeks = prvBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
                    weekId = prvBlockWeeks.at(prvBlock.weeks.length - 1)?.id;
                }
            } else {
                weekId = weeks.at(planState.weekIdx - 1)?.id;
            }
        }
        if (!weekId || !planState.plan.id) return;
        rerunManipulation(traineeId, planState.plan.id, weekId)
            .then(() => setReload(prv => !prv))
            .then(() => setEditMode(false))
            .then(() => notificationHandler.success("שבוע חושב מחדש"));
    };

    // Add exercises from dialog
    const handleAddExercisesFromDialog = (exerciseDefs) => {
        setWorkout(prv => {
            let newWorkout = { ...prv };
            let programExercises = [...newWorkout.programExercises].sort((a, b) => a.index > b.index ? 1 : -1);
            const isFirstBlockAndWeek = planState.blockIdx === 0 && planState.weekIdx === 0;
            const setType = isFirstBlockAndWeek ? "RM_SET" : "REGULAR_SET";

            exerciseDefs.forEach(def => {
                const isCardio = isCardioExercise(def);
                const isCatalyst = def.catalyst;
                if (isCatalyst) {
                    programExercises.push({
                        index: programExercises.length,
                        exerciseId: def.id,
                        exerciseName: def.nameHeb || def.name,
                        targetMuscle: def.targetMuscle,
                        manipulation: "RP_NEW",
                        restTime: 120,
                        tempo: null,
                        intervals: [
                            { index: 0, typeSet: setType, minDuration: 30, maxDuration: 60, recommendedDuration: 45 },
                            { index: 1, typeSet: setType, minDuration: 30, maxDuration: 60, recommendedDuration: 45 },
                            { index: 2, typeSet: setType, minDuration: 30, maxDuration: 60, recommendedDuration: 45 }
                        ]
                    });
                } else if (isCardio) {
                    programExercises.push({
                        index: programExercises.length,
                        exerciseId: def.id,
                        exerciseName: def.nameHeb || def.name,
                        targetMuscle: def.targetMuscle,
                        manipulation: null,
                        restTime: 0,
                        tempo: null,
                        durationTime: 60,
                        intervals: []
                    });
                } else {
                    const minReps = def.defaultMinReps ?? null;
                    const maxReps = def.defaultMaxReps ?? null;
                    const recReps = (minReps != null && maxReps != null) ? Math.round((minReps + maxReps) / 2) : 10;
                    programExercises.push({
                        index: programExercises.length,
                        exerciseId: def.id,
                        exerciseName: def.nameHeb || def.name,
                        targetMuscle: def.targetMuscle,
                        manipulation: "RP_NEW",
                        restTime: 120,
                        tempo: "T1_0_1_0",
                        intervals: [
                            { index: 0, typeSet: setType, recommendedReps: recReps, minReps, maxReps },
                            { index: 1, typeSet: setType, recommendedReps: recReps, minReps, maxReps },
                            { index: 2, typeSet: setType, recommendedReps: recReps, minReps, maxReps }
                        ]
                    });
                }
            });

            newWorkout.programExercises = programExercises;
            return newWorkout;
        });
        // Auto-expand newly added exercises
        setOpenIntervals(prev => {
            const updated = { ...prev };
            const startIdx = workout?.programExercises?.length || 0;
            exerciseDefs.forEach((_, i) => { updated[startIdx + i] = true; });
            return updated;
        });
    };

    // Add superset from dialog
    const handleAddSuperSetFromDialog = (defA, defB) => {
        setWorkout(prv => {
            let newWorkout = { ...prv };
            let programExercises = [...newWorkout.programExercises].sort((a, b) => a.index > b.index ? 1 : -1);
            const isFirstBlockAndWeek = planState.blockIdx === 0 && planState.weekIdx === 0;
            const setType = isFirstBlockAndWeek ? "RM_SET" : "REGULAR_SET";

            const makeIntervals = (def) => {
                const minReps = def.defaultMinReps ?? null;
                const maxReps = def.defaultMaxReps ?? null;
                const recReps = (minReps != null && maxReps != null) ? Math.round((minReps + maxReps) / 2) : 10;
                return [
                    { index: 0, typeSet: setType, recommendedReps: recReps, minReps, maxReps },
                    { index: 1, typeSet: setType, recommendedReps: recReps, minReps, maxReps },
                    { index: 2, typeSet: setType, recommendedReps: recReps, minReps, maxReps }
                ];
            };

            const exA = {
                index: programExercises.length,
                exerciseId: defA.id,
                exerciseName: defA.nameHeb || defA.name,
                targetMuscle: defA.targetMuscle,
                manipulation: "RP_NEW", restTime: 120, tempo: "T1_0_1_0",
                intervals: makeIntervals(defA)
            };
            const exB = {
                index: programExercises.length + 1,
                exerciseId: defB.id,
                exerciseName: defB.nameHeb || defB.name,
                targetMuscle: defB.targetMuscle,
                manipulation: "RP_NEW", restTime: 120, tempo: "T1_0_1_0",
                intervals: makeIntervals(defB)
            };

            const { exerciseA: linkedA, exerciseB: linkedB } = createSuperSetLink(exA, exB);
            programExercises.push(linkedA, linkedB);
            newWorkout.programExercises = programExercises;
            return newWorkout;
        });
        // Auto-expand newly added superset exercises
        setOpenIntervals(prev => {
            const updated = { ...prev };
            const startIdx = workout?.programExercises?.length || 0;
            updated[startIdx] = true;
            updated[startIdx + 1] = true;
            return updated;
        });
    };

    const handleUnlinkSuperSet = (exerciseA, exerciseB) => {
        setWorkout(prv => {
            let newWorkout = { ...prv };
            newWorkout.programExercises = newWorkout.programExercises.map(ex => {
                if (ex.index === exerciseA.index) return { ...ex, superSetGroupId: null };
                if (ex.index === exerciseB.index) return { ...ex, superSetGroupId: null };
                return ex;
            });
            return newWorkout;
        });
    };

    const handleLinkSuperSet = (exerciseA, exerciseB) => {
        setWorkout(prv => {
            let newWorkout = { ...prv };
            const sorted = [...newWorkout.programExercises].sort((a, b) => a.index > b.index ? 1 : -1);
            const aIdx = sorted.findIndex(ex => ex.index === exerciseA.index);
            const bIdx = sorted.findIndex(ex => ex.index === exerciseB.index);
            if (aIdx < 0 || bIdx < 0) return prv;

            const { exerciseA: linkedA, exerciseB: linkedB } = createSuperSetLink(sorted[aIdx], sorted[bIdx]);
            sorted[aIdx] = linkedA;
            sorted[bIdx] = linkedB;

            const [moved] = sorted.splice(bIdx, 1);
            const newAIdx = sorted.findIndex(ex => ex.index === linkedA.index);
            sorted.splice(newAIdx + 1, 0, moved);

            sorted.forEach((ex, i) => { ex.index = i; });
            newWorkout.programExercises = sorted;
            return newWorkout;
        });
    };

    const handleRemoveSuperSetBoth = (exerciseA, exerciseB) => {
        setWorkout(prv => {
            let newWorkout = { ...prv };
            let idx = 0;
            newWorkout.programExercises = newWorkout.programExercises
                .sort((a, b) => a.index > b.index ? 1 : -1)
                .filter(x => x.index !== exerciseA.index && x.index !== exerciseB.index)
                .map(val => { val.index = idx; idx += 1; return val; });
            return newWorkout;
        });
    };

    const expandAllIntervals = () => {
        const newOpenIntervals = {};
        workout.programExercises?.forEach(exercise => {
            if (exercise.targetMuscle !== 'CARDIO') {
                const key = exercise.uuid || exercise.id || exercise.index;
                newOpenIntervals[key] = true;
            }
        });
        setOpenIntervals(newOpenIntervals);
    };

    const collapseAllIntervals = () => { setOpenIntervals({}); };

    const areAllIntervalsExpanded = () => {
        const nonCardio = workout?.programExercises?.filter(ex => ex.targetMuscle !== 'CARDIO') || [];
        if (nonCardio.length === 0) return false;
        return nonCardio.every(ex => openIntervals?.[ex.uuid || ex.id || ex.index]);
    };

    const validateWorkout = () => {
        const errors = [];
        workout.programExercises?.forEach((exercise, index) => {
            const num = index + 1;
            if (!exercise.restTime && exercise.restTime !== 0) errors.push(`תרגיל ${num}: חסר זמן מנוחה`);
            if (exercise.targetMuscle !== 'CARDIO' && (!exercise.intervals || exercise.intervals.length === 0)) {
                errors.push(`תרגיל ${num}: חסרים סטים`);
            }
        });
        return errors;
    };

    const updateAllManipulations = (value) => {
        setWorkout(prv => {
            let newWorkout = { ...prv };
            newWorkout.programExercises = newWorkout.programExercises.map(exercise => ({ ...exercise, manipulation: value }));
            return newWorkout;
        });
    };

    const handleSave = () => {
        const errors = validateWorkout();
        if (errors.length > 0) { notificationHandler.error(errors.join('\n')); return; }
        setLoading(true);
        updateProgram({ ...workout, traineeId: traineeId })
            .then(() => setReload(prv => !prv))
            .then(() => setEditMode(false))
            .then(() => onReload?.())
            .then(() => notificationHandler.success("תוכנית נשמרה בהצלחה"))
            .then(() => setLoading(false))
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)))
            .catch(() => setLoading(false));
    };

    // Group exercises for card rendering
    const sortedExercises = workout?.programExercises?.sort((a, b) => a.index > b.index ? 1 : -1) || [];
    const groups = groupExercisesForRendering(sortedExercises);

    return (
        <>
            {workout && <>
                {/* Toolbar */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <RTL>
                            <Button
                                onClick={areAllIntervalsExpanded() ? collapseAllIntervals : expandAllIntervals}
                                startIcon={areAllIntervalsExpanded() ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                                sx={{ color: '#64748B', fontSize: '13px', fontWeight: 500, padding: '6px 12px', borderRadius: '6px', '&:hover': { backgroundColor: '#F1F5F9' } }}
                                variant="text"
                            >
                                {areAllIntervalsExpanded() ? "סגור הכל" : "פתח הכל"}
                            </Button>
                        </RTL>

                        {/* View toggle */}
                        <Tooltip title={viewMode === 'cards' ? "תצוגת סיכום" : "תצוגת כרטיסים"}>
                            <IconButton onClick={() => setViewMode(v => v === 'cards' ? 'summary' : 'cards')}
                                sx={{ color: '#64748B', '&:hover': { backgroundColor: '#F1F5F9' } }}>
                                {viewMode === 'cards' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>

                        {/* Bulk manipulation in edit mode */}
                        {editMode && (
                            <RTL>
                                <Select
                                    size="small" displayEmpty value=""
                                    onChange={(e) => updateAllManipulations(e.target.value)}
                                    sx={{ fontSize: '12px', minWidth: '80px', height: '32px', '& .MuiSelect-select': { padding: '4px 8px' } }}
                                >
                                    <MenuItem value="" disabled>מניפולציה הכל</MenuItem>
                                    {ManipulationTypes.map((type, i) => <MenuItem key={i} value={type.value}>{type.label}</MenuItem>)}
                                </Select>
                            </RTL>
                        )}
                    </Box>
                </Box>

                {/* Exercise List - Cards or Summary */}
                {viewMode === 'summary' ? (
                    <ProgramSummaryView exercises={sortedExercises} />
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {groups.map((group) => {
                            const draggable = editMode && !group.exercise?.superSetGroupId;
                            const idx = group.startIndex;

                            if (group.type === 'superset') {
                                return (
                                    <Box key={`ss-${group.exerciseA.id || group.exerciseA.uuid}-${group.exerciseB.id || group.exerciseB.uuid}`}
                                        draggable={editMode}
                                        onDragStart={(e) => handleDragStart(e, idx)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, idx)}
                                        onDrop={(e) => handleDrop(e, idx)}
                                        sx={{
                                            position: 'relative',
                                            cursor: editMode ? 'grab' : 'default',
                                            '&:active': { cursor: editMode ? 'grabbing' : 'default' },
                                            opacity: draggedIndex === idx ? 0.5 : 1,
                                            transition: 'opacity 0.2s',
                                        }}
                                    >
                                        {dragOverIndex === idx && draggedIndex !== idx && (
                                            <Box sx={{ position: 'absolute', top: -2, left: 0, right: 0, height: 3, backgroundColor: '#7C3AED', borderRadius: 1, zIndex: 10 }} />
                                        )}
                                    <ProgramSuperSetCard
                                        exerciseA={group.exerciseA}
                                        exerciseB={group.exerciseB}
                                        editMode={editMode}
                                        setEditWorkout={setWorkout}
                                        prvWorkout={prvWorkout}
                                        prvWorkoutIdx={workoutIdx}
                                        numberOfElements={sortedExercises.length}
                                        blockIdx={planState.blockIdx}
                                        weekIdx={planState.weekIdx}
                                        openIntervals={openIntervals}
                                        setOpenIntervals={setOpenIntervals}
                                        traineeId={traineeId}
                                        onUnlink={() => handleUnlinkSuperSet(group.exerciseA, group.exerciseB)}
                                        onRemoveBoth={() => handleRemoveSuperSetBoth(group.exerciseA, group.exerciseB)}
                                    />
                                    </Box>
                                );
                            }
                            return (
                                <Box key={group.exercise.id || group.exercise.uuid || group.startIndex}
                                    draggable={draggable}
                                    onDragStart={(e) => draggable && handleDragStart(e, idx)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => handleDragOver(e, idx)}
                                    onDrop={(e) => handleDrop(e, idx)}
                                    sx={{
                                        position: 'relative',
                                        cursor: editMode ? 'grab' : 'default',
                                        '&:active': { cursor: editMode ? 'grabbing' : 'default' },
                                        opacity: draggedIndex === idx ? 0.5 : 1,
                                        transition: 'opacity 0.2s',
                                    }}
                                >
                                    {dragOverIndex === idx && draggedIndex !== idx && (
                                        <Box sx={{ position: 'absolute', top: -2, left: 0, right: 0, height: 3, backgroundColor: 'primary.main', borderRadius: 1, zIndex: 10 }} />
                                    )}
                                <ProgramExerciseCard
                                    exercise={group.exercise}
                                    editMode={editMode}
                                    setEditWorkout={setWorkout}
                                    prvWorkout={prvWorkout}
                                    prvWorkoutIdx={workoutIdx}
                                    numberOfElements={sortedExercises.length}
                                    blockIdx={planState.blockIdx}
                                    weekIdx={planState.weekIdx}
                                    openIntervals={openIntervals}
                                    setOpenIntervals={setOpenIntervals}
                                    traineeId={traineeId}
                                    allExercises={sortedExercises}
                                    onLinkAsSuperSet={(partner) => handleLinkSuperSet(group.exercise, partner)}
                                />
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Action buttons */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                    gap: { xs: 2, md: 1 },
                    mt: 2
                }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                        {/* Edit / Add Exercise button */}
                        <Button
                            disabled={!planState?.plan?.active || workout?.stateType === "COMPLETED"}
                            onClick={() => editMode ? setIsAddChooserOpen(true) : setEditMode(true)}
                            sx={{
                                backgroundColor: '#1E293B', fontSize: { xs: '12px', md: '13px' }, fontWeight: 600,
                                padding: { xs: '6px 12px', md: '8px 16px' }, borderRadius: '6px',
                                '&:hover': { backgroundColor: '#334155' },
                                '&:disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                            }}
                            variant="contained"
                        >
                            {editMode ? "הוסף תרגיל +" : "ערוך"}
                        </Button>

                        {workout?.stateType !== "COMPLETED" && (
                            <Button
                                disabled={!planState?.plan?.active}
                                onClick={() => handleFinishWorkout(workout.id)}
                                sx={{
                                    backgroundColor: '#22C55E', fontSize: { xs: '12px', md: '13px' }, fontWeight: 600,
                                    padding: { xs: '6px 12px', md: '8px 16px' }, borderRadius: '6px',
                                    '&:hover': { backgroundColor: '#16A34A' },
                                    '&:disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                                }}
                                variant="contained"
                            >
                                סיים אימון
                            </Button>
                        )}

                        <Button
                            disabled={!planState?.plan?.active}
                            onClick={() => {
                                const blocks = planState?.plan?.blocks?.sort((a, b) => a.index > b.index ? 1 : -1);
                                const curBlock = blocks?.at(planState.blockIdx);
                                const weeks = curBlock?.weeks?.sort((a, b) => a.index > b.index ? 1 : -1);
                                const curWeek = weeks?.at(planState.weekIdx);
                                if (curWeek?.id) handleResetWeek(curWeek.id);
                            }}
                            sx={{
                                backgroundColor: '#FCFCFD', color: '#64748B', border: '1px solid #E2E8F0',
                                fontSize: { xs: '12px', md: '13px' }, fontWeight: 600,
                                padding: { xs: '6px 12px', md: '8px 16px' }, borderRadius: '6px',
                                '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' },
                                '&:disabled': { backgroundColor: '#F8FAFC', color: '#CBD5E1', borderColor: '#E2E8F0' }
                            }}
                            variant="outlined"
                        >
                            אפס שבוע
                        </Button>

                        <Button
                            disabled={!planState?.plan?.active || isAnyWorkoutNotHasFinishedOrWeekIsFinished() || !isLastWeekFinished()}
                            onClick={() => handleRerunManipulation()}
                            sx={{
                                backgroundColor: '#FCFCFD', color: '#64748B', border: '1px solid #E2E8F0',
                                fontSize: { xs: '11px', md: '13px' }, fontWeight: 600,
                                padding: { xs: '6px 10px', md: '8px 16px' }, borderRadius: '6px',
                                '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1' },
                                '&:disabled': { backgroundColor: '#F8FAFC', color: '#CBD5E1', borderColor: '#E2E8F0' }
                            }}
                            variant="outlined"
                        >
                            הפעל מניפולציה
                        </Button>

                        {editMode && (
                            <Button
                                onClick={() => { setReload(prv => !prv); setEditMode(false); }}
                                sx={{
                                    color: '#EF4444', fontSize: { xs: '12px', md: '13px' }, fontWeight: 600,
                                    padding: { xs: '6px 12px', md: '8px 16px' }, borderRadius: '6px',
                                    '&:hover': { backgroundColor: '#FEF2F2' }
                                }}
                                variant="text"
                            >
                                בטל
                            </Button>
                        )}
                    </Box>

                    {editMode && (
                        <Button
                            disabled={(() => {
                                const blocks = planState?.plan?.blocks?.sort((a, b) => a.index > b.index ? 1 : -1);
                                const curBlock = blocks?.at(planState.blockIdx);
                                const weeks = curBlock?.weeks?.sort((a, b) => a.index > b.index ? 1 : -1);
                                const curWeek = weeks?.at(planState.weekIdx);
                                return curWeek?.finished || false;
                            })()}
                            onClick={handleSave}
                            sx={{
                                backgroundColor: '#3B82F6', fontSize: { xs: '12px', md: '13px' }, fontWeight: 600,
                                padding: { xs: '8px 16px', md: '8px 20px' }, borderRadius: '6px',
                                width: { xs: '100%', md: 'auto' },
                                '&:hover': { backgroundColor: '#2563EB' },
                                '&:disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                            }}
                            variant="contained"
                        >
                            שמור ושלח
                        </Button>
                    )}
                </Box>

                {/* Add Chooser Dialog */}
                <Dialog
                    open={isAddChooserOpen}
                    onClose={() => setIsAddChooserOpen(false)}
                    PaperProps={{ sx: { borderRadius: '14px', minWidth: { xs: '90%', sm: 640 }, maxWidth: 720, p: 0 } }}
                >
                    <DialogContent sx={{ p: 3, direction: 'rtl' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#1E293B', textAlign: 'center', mb: 3 }}>
                            מה תרצה להוסיף?
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Box onClick={() => { setIsAddChooserOpen(false); setIsAddExercisesOpen(true); }}
                                sx={{
                                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 3,
                                    borderRadius: '12px', border: '2px solid #E2E8F0', cursor: 'pointer', transition: 'all 0.2s ease',
                                    '&:hover': { borderColor: '#57837E', backgroundColor: '#F0FAF8', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(87, 131, 126, 0.15)' }
                                }}>
                                <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#F0FAF8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FitnessCenterIcon sx={{ fontSize: 28, color: '#57837E' }} />
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1E293B' }}>תרגילים</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.3 }}>הוסף תרגיל אחד או יותר</Typography>
                            </Box>
                            {/* Add Cardio Option */}
                            <Box onClick={() => { setIsAddChooserOpen(false); setIsAddCardioOpen(true); }}
                                sx={{
                                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 3,
                                    borderRadius: '12px', border: '2px solid #E2E8F0', cursor: 'pointer', transition: 'all 0.2s ease',
                                    '&:hover': { borderColor: '#E91E63', backgroundColor: '#FFF0F5', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(233, 30, 99, 0.15)' }
                                }}>
                                <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#FFF0F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DirectionsRunIcon sx={{ fontSize: 28, color: '#E91E63' }} />
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1E293B' }}>קרדיו</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.3 }}>הוסף תרגיל קרדיו</Typography>
                            </Box>
                            {/* Add Catalyst Option */}
                            <Box onClick={() => { setIsAddChooserOpen(false); setIsAddCatalystOpen(true); }}
                                sx={{
                                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 3,
                                    borderRadius: '12px', border: '2px solid #E2E8F0', cursor: 'pointer', transition: 'all 0.2s ease',
                                    '&:hover': { borderColor: '#FF6B00', backgroundColor: '#FFF8F0', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(255, 107, 0, 0.15)' }
                                }}>
                                <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#FFF8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <TimerIcon sx={{ fontSize: 28, color: '#FF6B00' }} />
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1E293B' }}>תרגיל מבוסס זמן</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.3 }}>סטים מבוססי זמן</Typography>
                            </Box>
                            <Box
                                onClick={isSuperSetEnabled ? () => { setIsAddChooserOpen(false); setIsAddSuperSetOpen(true); } : undefined}
                                sx={{
                                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 3,
                                    borderRadius: '12px', border: '2px solid #E2E8F0',
                                    ...(isSuperSetEnabled
                                        ? { cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: SUPERSET_COLORS.accent, backgroundColor: '#FAF5FF', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.15)' } }
                                        : { opacity: 0.5, cursor: 'default' }),
                                }}>
                                <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: isSuperSetEnabled ? '#FAF5FF' : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <SyncAltIcon sx={{ fontSize: 28, color: isSuperSetEnabled ? SUPERSET_COLORS.accent : '#CBD5E1' }} />
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: isSuperSetEnabled ? '#1E293B' : '#94A3B8' }}>סופר סט</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: isSuperSetEnabled ? '#94A3B8' : '#CBD5E1', textAlign: 'center', lineHeight: 1.3 }}>
                                    {isSuperSetEnabled ? 'הוסף זוג תרגילים כסופר סט' : 'בקרוב'}
                                </Typography>
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* Dialogs */}
                <AddExercisesDialog
                    isOpen={isAddExercisesOpen}
                    close={() => setIsAddExercisesOpen(false)}
                    onAdd={handleAddExercisesFromDialog}
                    excludeCardio
                />
                <AddExercisesDialog
                    isOpen={isAddCardioOpen}
                    close={() => setIsAddCardioOpen(false)}
                    onAdd={handleAddExercisesFromDialog}
                    title="הוסף תרגיל קרדיו"
                    initialTargetMuscle={{ value: "CARDIO", name: "קרדיו" }}
                    hideFilter
                />
                <AddExercisesDialog
                    isOpen={isAddCatalystOpen}
                    close={() => setIsAddCatalystOpen(false)}
                    onAdd={handleAddExercisesFromDialog}
                    title="הוסף תרגיל מבוסס זמן"
                    catalystOnly
                />
                <AddSuperSetDialog
                    isOpen={isAddSuperSetOpen}
                    close={() => setIsAddSuperSetOpen(false)}
                    onAdd={handleAddSuperSetFromDialog}
                />
            </>}
        </>
    );
};

export default WorkoutActiveTab;
